import { useCallback, useState } from "react";

import {
  PassageTargetType,
  ProgressType,
  useCreateProgramReviewTargetMutation,
  useDeleteProgramReviewTargetMutation,
} from "@api/graphql/types-and-hooks";
import { ProgramReviewGoalList, useProgrammingReviewData } from "@providers/ProgrammingReviewProvider";

interface UseProgrammingReviewTargetsProps {
  clientId: string | undefined;
  programReviewGoalList: ProgramReviewGoalList;
  refetchProgramReviewGoals: () => Promise<unknown>;
}

export const useProgrammingReviewTargets = ({
  clientId,
  programReviewGoalList,
  refetchProgramReviewGoals,
}: UseProgrammingReviewTargetsProps) => {
  const { setSelectedProgramReviewGoal, setSelectedProgramReviewTarget } = useProgrammingReviewData();

  // Delete confirmation dialog state
  const [isDeleteConfirmDialogOpen, setIsDeleteConfirmDialogOpen] = useState(false);
  const [targetToDelete, setTargetToDelete] = useState<{ id: string; title: string } | null>(null);

  // Mutations
  const createTargetMutation = useCreateProgramReviewTargetMutation();
  const deleteTargetMutation = useDeleteProgramReviewTargetMutation();

  const handleSelectTarget = useCallback(
    (targetId: string) => {
      for (const goal of programReviewGoalList ?? []) {
        const targetEdge = goal.passageTargets?.edges?.find((edge) => edge?.node?.id === targetId);
        if (targetEdge?.node) {
          setSelectedProgramReviewTarget(targetEdge.node as PassageTargetType);
          return;
        }
      }
    },
    [programReviewGoalList, setSelectedProgramReviewTarget],
  );

  const handleSelectGoal = useCallback(
    (goalId: string) => {
      setSelectedProgramReviewTarget(null);
      const goal = programReviewGoalList?.find((g) => g.id === goalId);
      if (goal) {
        setSelectedProgramReviewGoal(goal);
      }
    },
    [programReviewGoalList, setSelectedProgramReviewGoal, setSelectedProgramReviewTarget],
  );

  const handleDeleteTarget = useCallback(
    (targetId: string) => {
      for (const goal of programReviewGoalList ?? []) {
        const targetEdge = goal.passageTargets?.edges?.find((edge) => edge?.node?.id === targetId);
        if (targetEdge) {
          setTargetToDelete({ id: targetId, title: targetEdge.node?.title ?? "" });
          setIsDeleteConfirmDialogOpen(true);
          return;
        }
      }
    },
    [programReviewGoalList],
  );

  const confirmDeleteTarget = useCallback(async () => {
    if (!targetToDelete) return;

    try {
      await deleteTargetMutation.mutateAsync({ targetId: targetToDelete.id }).then((response) => {
        if (response.deleteProgramReviewTarget?.status) {
          setSelectedProgramReviewTarget(null);
          refetchProgramReviewGoals();
          setIsDeleteConfirmDialogOpen(false);
          setTargetToDelete(null);
        }
      });
    } catch (error) {
      console.error("Error deleting target:", error);
    }
  }, [targetToDelete, deleteTargetMutation, refetchProgramReviewGoals, setSelectedProgramReviewTarget]);

  const cancelDeleteTarget = useCallback(() => {
    setIsDeleteConfirmDialogOpen(false);
    setTargetToDelete(null);
  }, []);

  const handleOpenCustomTargetForm = useCallback(
    async (goalId: string) => {
      const goal = programReviewGoalList?.find((g) => g.id === goalId);
      if (!goal) return;

      try {
        const response = await createTargetMutation.mutateAsync({
          input: {
            learnerId: clientId ?? "",
            goalId: goalId,
            title: "New Target",
            description: "Enter description",
            type: ProgressType.Trial,
          },
        });

        if (response.createProgramReviewTarget?.status && response.createProgramReviewTarget.target) {
          const createdTargetId = response.createProgramReviewTarget.target.id;

          await refetchProgramReviewGoals();

          setTimeout(() => {
            handleSelectTarget(createdTargetId);
          }, 100);
        }
      } catch (error) {
        console.error("Error creating target:", error);
      }
    },
    [clientId, programReviewGoalList, createTargetMutation, refetchProgramReviewGoals, handleSelectTarget],
  );

  const handleOpenSuggestedTargetForm = useCallback(
    (goalId: string) => {
      // TODO: Implement suggested target logic
      handleOpenCustomTargetForm(goalId);
    },
    [handleOpenCustomTargetForm],
  );

  return {
    // Target operations
    handleSelectTarget,
    handleSelectGoal,
    handleDeleteTarget,
    handleOpenCustomTargetForm,
    handleOpenSuggestedTargetForm,

    // Delete dialog
    isDeleteConfirmDialogOpen,
    setIsDeleteConfirmDialogOpen,
    targetToDelete,
    confirmDeleteTarget,
    cancelDeleteTarget,

    // Loading states
    isCreatingTarget: createTargetMutation.isPending,
    isDeletingTarget: deleteTargetMutation.isPending,
  };
};
